<script>
import { pluralize } from '@utils/Format'

// Paleta específica da barra escura de ações em lote — sem token global
// equivalente (--white/--danger são pensados pra fundo claro). Valores
// extraídos do componente BulkBar da referência "novo design 2026"
// (ui.jsx:200-217). Expressos como hex 8 dígitos (não rgba()) porque
// IconSVG.isCssColorValue só reconhece `var(`/`#`/`currentColor` como
// literal — um `rgba(...)` cru vira `var(--rgba(...))`, inválido. #RRGGBBAA
// é o mesmo valor (confirmado: o browser computa rgba(255,255,255,.7) como
// exatamente #FFFFFFB3).
const ON_DARK_ICON = '#FFFFFFCC'
const ON_DARK_SECONDARY = '#FFFFFFB3'
const DANGER_ON_DARK = '#FF8A8A'

// Máximo de ações não-destrutivas visíveis na barra desktop antes de
// estourar automaticamente pro menu de overflow (⋮) — sem prop, fixo.
const VISIBLE_ACTIONS_LIMIT = 3

// Margem mínima (px) mantida entre a barra e a borda da viewport ao
// arrastar, pra nunca deixar ela sair completamente de vista.
const DRAG_VIEWPORT_MARGIN = 24

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    hideBatch: {
      type: Boolean,
      default: false,
    },
    /**
     * Acende a barra sem depender de `value.length`. Existe para o consumidor
     * cujo gatilho é um booleano (ex.: "há edição pendente na planilha"), e
     * não uma lista de selecionados — nesse caso `value` fica vazio e a
     * condição de visibilidade padrão nunca seria satisfeita. Não substitui
     * `hideBatch`, que continua tendo a última palavra.
     */
    forceVisible: {
      type: Boolean,
      default: false,
    },
    /**
     * Esconde o botão `✕`. Quando a barra não representa uma seleção não há
     * o que "limpar" — e um `✕` que só some junto com a barra deixaria a
     * alteração pendente invisível. Negativa (default `false`) porque o lint
     * do repo proíbe booleana com default `true`.
     */
    hideClear: {
      type: Boolean,
      default: false,
    },
    tipo: {
      type: String,
      default: 'itens',
    },
    tipoSingular: {
      type: String,
      default: '',
    },
    /**
     * { loading, action, icon, title, submenu?, danger? }
     * `submenu` não é mais lido pelo componente (mantido só por
     * compatibilidade com quem já passa esse campo) — a barra desktop
     * decide sozinha o que fica visível: as `VISIBLE_ACTIONS_LIMIT`
     * primeiras ações não-destrutivas (ordem do array) ficam visíveis, o
     * resto vai automaticamente pro menu `⋮`. `danger: true` estiliza a
     * ação em vermelho (ex: Excluir) e fica sempre visível, agrupada após
     * o `⋮`, separada por um divisor. No mobile todas as ações aparecem no
     * grid, sem corte.
     */
    acoes: {
      type: Array,
      default: () => [],
    },
    selectText: {
      type: String,
      default: 'selecionados',
    },
    selectTextSingular: {
      type: String,
      default: 'selecionado',
    },
  },
  data() {
    return {
      pluralize,
      ON_DARK_ICON,
      ON_DARK_SECONDARY,
      dragOffset: { x: 0, y: 0 },
      isDragging: false,
      dragStart: { x: 0, y: 0, offsetX: 0, offsetY: 0 },
    }
  },
  computed: {
    singular() {
      return this.tipoSingular || this.tipo
    },
    nonDangerActions() {
      return this.acoes.filter((item) => !item.danger)
    },
    // Barra desktop: só as N primeiras (ordem do array) ficam visíveis, o
    // resto estoura pro menu de overflow — automático, ignora `submenu`.
    menuPrincipalNormal() {
      return this.nonDangerActions.slice(0, VISIBLE_ACTIONS_LIMIT)
    },
    submenu() {
      return this.nonDangerActions.slice(VISIBLE_ACTIONS_LIMIT)
    },
    menuPrincipalDanger() {
      return this.acoes.filter((item) => item.danger)
    },
    dragTransform() {
      return `translate(${this.dragOffset.x}px, ${this.dragOffset.y}px)`
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onDragMove)
    document.removeEventListener('mouseup', this.stopDrag)
  },
  methods: {
    acaoColor(acao) {
      return acao.danger ? DANGER_ON_DARK : ON_DARK_ICON
    },
    startDrag(event) {
      if (this.isDragging) return
      this.isDragging = true
      this.dragStart = {
        x: event.clientX,
        y: event.clientY,
        offsetX: this.dragOffset.x,
        offsetY: this.dragOffset.y,
      }
      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.stopDrag)
    },
    onDragMove(event) {
      if (!this.isDragging) return
      const deltaX = event.clientX - this.dragStart.x
      const deltaY = event.clientY - this.dragStart.y
      this.dragOffset = this.clampOffset(
        this.dragStart.offsetX + deltaX,
        this.dragStart.offsetY + deltaY
      )
    },
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.stopDrag)
    },
    // Mantém a barra dentro da viewport (com uma margem). Usa a posição
    // atual do card MENOS o offset já aplicado pra achar a posição "natural"
    // (sem drag), que não muda durante o gesto — referência estável pros
    // limites min/max a cada movimento.
    clampOffset(x, y) {
      const card = this.$refs.card
      if (!card || typeof card.getBoundingClientRect !== 'function') {
        return { x, y }
      }
      const rect = card.getBoundingClientRect()
      const naturalLeft = rect.left - this.dragOffset.x
      const naturalTop = rect.top - this.dragOffset.y
      const minX = DRAG_VIEWPORT_MARGIN - naturalLeft
      const maxX =
        window.innerWidth - DRAG_VIEWPORT_MARGIN - rect.width - naturalLeft
      const minY = DRAG_VIEWPORT_MARGIN - naturalTop
      const maxY =
        window.innerHeight - DRAG_VIEWPORT_MARGIN - rect.height - naturalTop
      return {
        x: Math.min(Math.max(x, minX), maxX),
        y: Math.min(Math.max(y, minY), maxY),
      }
    },
  },
}
</script>

<template>
  <div
    v-if="(value.length || forceVisible) && !hideBatch"
    key="acoes-lote"
    class="full-center"
  >
    <v-scale-transition>
      <div class="acao-lote-container">
        <div
          ref="card"
          class="acao-lote-card"
          :style="{ transform: dragTransform }"
        >
          <!-- Versão Desktop -->
          <div class="acao-lote-desktop d-none d-lg-flex align-center pa-2">
            <span
              class="acao-lote-drag-handle d-flex align-center justify-center mr-1"
              :class="{ 'acao-lote-drag-handle--dragging': isDragging }"
              aria-hidden="true"
              @mousedown="startDrag"
            >
              <icon-svg
                name="mdi-drag-vertical"
                size="16"
                :color="isDragging ? ON_DARK_ICON : '#FFFFFF80'"
              />
            </span>

            <!--
              Slot `label`: substitui o bloco do contador inteiro (badge +
              texto pluralizado), inclusive o contêiner — `.acao-lote-count`
              tem `max-width: 170px`, largura pensada para "N itens
              selecionados" e apertada demais para uma frase. O fallback é
              exatamente o markup anterior, então quem não passa o slot não vê
              diferença. O slot é renderizado nas DUAS versões (desktop e
              mobile): cada uma monta sua própria árvore, verificado em teste.
            -->
            <slot name="label">
              <div class="acao-lote-count d-flex align-center px-4">
                <span class="acao-lote-count__badge mr-2">{{
                  value.length
                }}</span>
                <span class="acao-lote-count__label">
                  {{ pluralize(value.length, singular, tipo) }}
                  {{ pluralize(value.length, selectTextSingular, selectText) }}
                </span>
              </div>
            </slot>

            <!--
              Só há o que separar se existir ação visível logo depois: sem
              ação não-destrutiva — `acoes` vazio (caso do wrapper
              `CommissionSaveBar`, que usa a barra só como moldura de um
              rótulo) ou só ações `danger` — o divisor ficaria pendurado ou
              duplicado, já que o bloco danger e o `✕` trazem o divisor deles
              próprios, mais abaixo.
            -->
            <div
              v-if="menuPrincipalNormal.length"
              class="acao-lote-divider my-2 mx-1"
            ></div>

            <div class="acao-lote-actions d-flex align-center">
              <template v-for="acao in menuPrincipalNormal">
                <BaseButton
                  :key="acao.title"
                  none
                  fit-content
                  min-width="0"
                  :loading="acao.loading"
                  class="acao-lote-btn"
                  @click="acao.action"
                >
                  <icon-svg
                    :name="acao.icon"
                    size="16"
                    :color="acaoColor(acao)"
                  />
                  <span>{{ acao.title }}</span>
                </BaseButton>
              </template>
            </div>

            <v-menu
              v-if="submenu.length"
              offset-y
              top
              :nudge-top="12"
              content-class="acao-lote-submenu"
            >
              <template v-slot:activator="{ on, attrs }">
                <BaseButton
                  none
                  fit-content
                  min-width="0"
                  class="acao-lote-btn"
                  aria-label="Mais ações"
                  v-bind="attrs"
                  v-on="on"
                >
                  <icon-svg
                    name="mdi-dots-vertical"
                    size="18"
                    :color="ON_DARK_ICON"
                  />
                </BaseButton>
              </template>
              <div class="acao-lote-submenu__panel">
                <div class="acao-lote-submenu__list">
                  <button
                    v-for="acao in submenu"
                    :key="acao.title"
                    type="button"
                    class="acao-lote-submenu__item"
                    :class="{ 'acao-lote-submenu__item--danger': acao.danger }"
                    @click="acao.action"
                  >
                    <icon-svg
                      :name="acao.icon"
                      :size="15"
                      :color="acaoColor(acao)"
                    />
                    {{ acao.title }}
                  </button>
                </div>
              </div>
            </v-menu>

            <template v-if="menuPrincipalDanger.length">
              <div class="acao-lote-divider my-2 mx-1"></div>

              <template v-for="acao in menuPrincipalDanger">
                <BaseButton
                  :key="acao.title"
                  none
                  fit-content
                  min-width="0"
                  :loading="acao.loading"
                  class="acao-lote-btn acao-lote-btn--danger"
                  @click="acao.action"
                >
                  <icon-svg
                    :name="acao.icon"
                    size="16"
                    :color="acaoColor(acao)"
                  />
                  <span>{{ acao.title }}</span>
                </BaseButton>
              </template>
            </template>

            <template v-if="!hideClear">
              <div class="acao-lote-divider my-2 mx-1"></div>

              <BaseButton
                none
                fit-content
                min-width="0"
                class="acao-lote-btn acao-lote-btn--clear"
                aria-label="Limpar seleção"
                @click="$emit('input', [])"
              >
                <icon-svg
                  name="close-circle"
                  size="18"
                  :color="ON_DARK_SECONDARY"
                />
              </BaseButton>
            </template>
          </div>

          <!-- Versão Mobile/Tablet -->
          <div class="acao-lote-mobile d-flex d-lg-none pa-4 flex-column">
            <div
              class="d-flex align-center justify-space-between"
              :class="{ 'mb-2': acoes.length }"
            >
              <slot name="label">
                <span class="acao-lote-count__label">
                  <span class="acao-lote-count__badge mr-2">{{
                    value.length
                  }}</span>
                  {{ pluralize(value.length, singular, tipo) }}
                  {{ pluralize(value.length, selectTextSingular, selectText) }}
                </span>
              </slot>

              <BaseButton
                v-if="!hideClear"
                none
                fit-content
                min-width="0"
                class="acao-lote-btn acao-lote-btn--clear"
                aria-label="Limpar seleção"
                @click="$emit('input', [])"
              >
                <icon-svg
                  name="close-circle"
                  size="16"
                  :color="ON_DARK_SECONDARY"
                />
              </BaseButton>
            </div>

            <div v-if="acoes.length" class="acao-grid-mobile">
              <template v-for="acao in acoes">
                <BaseButton
                  :key="acao.title"
                  none
                  :loading="acao.loading"
                  class="acao-mobile-btn"
                  :class="{ 'acao-lote-btn--danger': acao.danger }"
                  content-class="d-flex flex-column align-center justify-center"
                  @click="acao.action"
                >
                  <icon-svg
                    :name="acao.icon"
                    size="16"
                    :color="acaoColor(acao)"
                  />
                  <span class="caption mt-1">
                    {{ acao.title }}
                  </span>
                </BaseButton>
              </template>
            </div>
          </div>
        </div>
      </div>
    </v-scale-transition>
  </div>
</template>

<style lang="sass">
// Paleta específica do fundo escuro da barra (BulkBar da referência "novo
// design 2026", ui.jsx) — sem token global equivalente. Mantida em sincronia
// manual com as constantes ON_DARK_*/DANGER_ON_DARK do <script>.
$on-dark-hover: rgba(255, 255, 255, .1)
$on-dark-divider: rgba(255, 255, 255, .14)
$danger-on-dark: #FF8A8A
$danger-on-dark-hover: rgba(239, 68, 68, .18)

.acao-lote-container
  left: 0
  right: 0
  bottom: 20px
  width: fit-content
  position: fixed
  margin: auto
  z-index: 99

  // Desktop
  @media (min-width: 1264px)
    padding-left: 126px

  // Tablet
  @media (max-width: 1263px)
    width: auto
    max-width: calc(100vw - 100px)
    padding-left: 86px

  // Mobile
  @media (max-width: 600px)
    left: 10px
    right: 10px
    width: auto
    max-width: calc(100vw - 20px)
    padding-left: 0px

.acao-lote-card
  background: var(--neutral-950)
  border-radius: var(--radius-lg)
  box-shadow: 0 16px 44px -10px rgba(0, 0, 0, .5)

// Sem isso, quando o container esbarra no max-width (breakpoint tablet, ou
// viewport reduzida por ex. pelo DevTools docked), os itens do fim da barra
// (⋮, Excluir, fechar) ficam visualmente perdidos além do fundo escuro — só
// o miolo de ações (.acao-lote-actions) tinha scroll próprio, a linha
// inteira não. Agora a barra toda rola horizontalmente se precisar, nunca
// corta nada.
.acao-lote-desktop
  overflow-x: auto
  overflow-y: hidden

  > *
    flex-shrink: 0

.acao-lote-drag-handle
  width: 28px
  height: 28px
  border-radius: var(--radius-sm)
  cursor: grab
  flex-shrink: 0
  transition: background-color 0.15s ease

  &:hover
    background-color: $on-dark-hover

  &--dragging
    cursor: grabbing
    background-color: $on-dark-hover

.acao-lote-count
  max-width: 170px

.acao-lote-count__badge
  display: inline-flex
  align-items: center
  justify-content: center
  min-width: 22px
  height: 22px
  padding: 0 6px
  border-radius: var(--radius-sm)
  background: var(--primary)
  color: var(--text-on-primary)
  font-size: 12.5px
  font-weight: 700
  flex-shrink: 0

.acao-lote-count__label
  color: rgba(255, 255, 255, .7)
  font-size: 13.5px

.acao-lote-divider
  align-self: stretch
  width: 1px
  background: $on-dark-divider
  flex-shrink: 0

.acao-lote-actions
  min-width: 0

.acao-lote-btn
  color: rgba(255, 255, 255, .8)
  border-radius: var(--radius-md)
  padding: 10px 12px !important
  font-size: 13px
  font-weight: 500
  white-space: nowrap
  transition: background-color 0.15s ease

  &:hover
    background-color: $on-dark-hover

  &--danger
    color: $danger-on-dark

    &:hover
      background-color: $danger-on-dark-hover

  &--clear
    padding: 10px !important

// Menu de overflow copiado do padrão do _base-actions-menu.vue (botões
// <button> simples em vez de v-list/v-list-item — evita de vez as brigas de
// especificidade do tema padrão do Vuetify, ex.:
// `.theme--light.v-list-item:not(...):not(...) { color: rgba(0,0,0,.87)
// !important }`, que vencia qualquer override de cor que a gente tentasse
// no v-list-item). Mantém fundo/texto escuros (--neutral-900 / opacidade
// branca), só a estrutura do menu é reaproveitada.
.acao-lote-submenu
  border-radius: var(--radius-md)
  overflow: hidden

.acao-lote-submenu__panel
  background-color: var(--neutral-900)

.acao-lote-submenu__list
  padding: 4px

.acao-lote-submenu__item
  position: relative
  display: flex
  align-items: center
  gap: 8px
  width: 100%
  padding: 8px 10px
  font-family: Geist, sans-serif
  font-size: 13px
  color: rgba(255, 255, 255, .8)
  text-align: left
  cursor: pointer
  background: none
  border: 0
  border-radius: var(--radius-sm)

  &:hover
    background: rgba(255, 255, 255, .08)

  &:disabled
    cursor: not-allowed
    opacity: 0.6

  &--danger
    margin-top: 5px
    color: $danger-on-dark

    &::before
      position: absolute
      top: -3px
      right: 0
      left: 0
      height: 1px
      content: ''
      background: $on-dark-divider

  .acao-lote-submenu__title--danger
    color: $danger-on-dark !important

.acao-grid-mobile
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr))
  gap: 8px

  @media (max-width: 599px)
    grid-template-columns: repeat(3, 1fr)

  @media (min-width: 600px) and (max-width: 959px)
    grid-template-columns: repeat(4, 1fr)

  @media (min-width: 960px) and (max-width: 1263px)
    grid-template-columns: repeat(5, 1fr)

.acao-mobile-btn
  color: rgba(255, 255, 255, .8)
  height: 60px !important
  min-height: 60px !important
  padding: 8px !important
  border-radius: var(--radius-md)

  &:hover
    background-color: var(--neutral-900)

  &--danger
    color: $danger-on-dark

  .caption
    line-height: 1rem !important
    white-space: normal
    text-align: center
    color: inherit
</style>
